import Card from "@/components/common/Card";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { callApi, ThrowError } from "@/api/callApi";
import { isGuestDesignation, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import type { EmployeeCategory, EmployeeOnBoard, IEmployee, marrideStatus } from "@/interface/employeeOnBoard";
import OnboardBasicDetail from "./EmployeeOnboard/OnboardBasicDetail";
import OnboardPermissions from "./EmployeeOnboard/OnboardPermissions";
import OnboardAdvanceDetails from "./EmployeeOnboard/OnboardAdvanceDetails";
import OnboardAddress from "./EmployeeOnboard/OnboardAddress";
import OnboardBankDetails from "./EmployeeOnboard/OnboardBankDetails";
import OnboardNominee from "./EmployeeOnboard/OnboardNominee";
import OnboardClient from "./EmployeeOnboard/OnboardClient";
import OnboardSalaryDetail from "./EmployeeOnboard/OnboardSalaryDetail";
import OnboardDocuments from "./EmployeeOnboard/OnboardDocuments";

import { API_ENDPOINT } from "@/api/constant";
import { ChevronLeft, ChevronRight, Check, ShieldCheck, Lock, UserCheck } from "lucide-react";
import { useAppSelector } from "@/store/store";
import { Button } from "@/components/ui/button";
import CommonModal from "@/components/common/CommonModal";
import SearchSelect from "@/components/common/SearchSelect";
import { DesignationService } from "@/services/Designation/DesignationService";

import type { designation } from "@/interface/designation.interface";

import { ONBOARD_STEPS } from "./EmployeeOnboard/onboardConfig";
import { useOnboardProgress } from "./EmployeeOnboard/useOnboardProgress";
import type { CustomDocumentPayload } from "./EmployeeOnboard/onboard.types";
import { Spinner } from "@/components/ui/spinner";

// ─── Verification Modal State Interface ──────────────────────────────────────
interface VerifyModalState {
    isOpen: boolean;
    selectedDesignationId: string;
    error: string;
    designations: { value: string; label: string; level?: number }[];
}

const INITIAL_VERIFY_MODAL_STATE: VerifyModalState = {
    isOpen: false,
    selectedDesignationId: "",
    error: "",
    designations: [],
};

// ─── Tab Content Mapping ────────────────────────────────────────────────────
// Maps each tabKey to its React component for clean rendering
const TAB_CONTENT_MAP: Record<string, React.FC<any>> = {
    tab1: OnboardBasicDetail,
    tab2: OnboardPermissions,
    tab3: OnboardClient,
    tab4: OnboardAdvanceDetails,
    tab5: OnboardAddress,
    tab6: OnboardBankDetails,
    tab7: OnboardNominee,
    tab8: OnboardDocuments,
    tab9: OnboardSalaryDetail,
};

// ─── Shared tab trigger class ───────────────────────────────────────────────
const TAB_TRIGGER_CLASS =
    "rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white";

// ─── Component ──────────────────────────────────────────────────────────────
const CreateEmployee: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isProfile] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("tab1");
    const [createdId, setCreatedId] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSalaryCreated, setIsSalaryCreated] = useState<boolean | null>(null);
    const currentId = id || createdId;

    // ── Single Consolidated Verify Modal State ───────────────────────────
    const [verifyModal, setVerifyModal] = useState<VerifyModalState>(INITIAL_VERIFY_MODAL_STATE);

    console?.log("verifyModal", verifyModal)
    // ── Progress tracking via onboardingProcess ──────────────────────────
    const progress = useOnboardProgress();

    // ── Scrollable tabs ──────────────────────────────────────────────────
    const tabsRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkScrollButtons = () => {
        if (tabsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
            setShowLeftArrow(scrollLeft > 10);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scrollTabs = (direction: "left" | "right") => {
        if (tabsRef.current) {
            const scrollAmount = direction === "left" ? -250 : 250;
            tabsRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    useEffect(() => {
        checkScrollButtons();
        window.addEventListener("resize", checkScrollButtons);
        const timer = setTimeout(() => {
            if (tabsRef.current) {
                const activeTabElement = tabsRef.current.querySelector(`[data-state="active"]`);
                if (activeTabElement) {
                    activeTabElement.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                }
                checkScrollButtons();
            }
        }, 50);
        return () => {
            window.removeEventListener("resize", checkScrollButtons);
            clearTimeout(timer);
        };
    }, [activeTab]);

    // ── Auth ─────────────────────────────────────────────────────────────
    const loggedInUser = useAppSelector((state) => state.user.user);
    // console?.log("tabPropstabProps", loggedInUser)
    const loggedInUserId = loggedInUser?.id || "";

    // ── Helpers ──────────────────────────────────────────────────────────
    const formatDateForInput = (value?: string | null) =>
        value ? value.split("T")[0] : "";

    const normalizeEmployeeForForm = (employee: IEmployee): EmployeeOnBoard => {
        const userDetail = employee.userDetail;

        return {
            name: employee.name || "",
            email: employee.email?.toLowerCase()?.trim() || "",
            phone: employee.phone || "",
            secondaryPhone: employee.secondaryPhone || "",
            employeeCode: employee.employeeCode || "",
            category: (employee.category ?? "Monthly-Salary") as EmployeeCategory,
            type: String(employee.type ?? "0") as EmployeeOnBoard["type"],
            gender: String(employee.gender ?? "") as EmployeeOnBoard["gender"],
            status: (employee.status || "") as EmployeeOnBoard["status"],
            dob: formatDateForInput(employee.dob),
            joiningDate: formatDateForInput(employee.joiningDate),
            personalEmail: employee.personalEmail || "",
            onboardingProcess: employee.onboardingProcess || [],
            bloodGroup: employee.bloodGroup || "",
            profilePhoto: employee.profilePhoto || "",
            isMarried: (employee.isMarried as marrideStatus) || "",
            spouse: employee.spouse || userDetail?.spouse || "",
            children: employee.children ?? userDetail?.children ?? "",
            skillType: employee.skillType || "",
            permissions: Array.isArray(employee.permissions)
                ? employee.permissions.join(",")
                : employee.permissions || "",
            isAssignedAllSite: employee.isAssignedAllSite ?? false,
            assignedSiteType: employee.assignedSiteType || "",
            departmentId: employee.departmentId || "",
            designationId: employee.designationId || "",
            shiftId: employee.shiftId || "",
            siteId: employee.siteId || "",
            branchId: employee.branchId || "",
            fatherName: userDetail?.fatherName || "",
            motherName: userDetail?.motherName || "",
            uanNumber: userDetail?.uanNumber || "",
            aadharNumber: userDetail?.aadharNumber || "",
            esicNumber: userDetail?.esicNumber || "",
            insuranceNumber: userDetail?.insuranceNumber || "",
            pfNumber: userDetail?.pfNumber || "",
            panNumber: userDetail?.panNumber || "",
            drivingLicenseNumber: userDetail?.drivingLicenseNumber || "",
            address: userDetail?.address || "",
            secondaryAddress: userDetail?.secondaryAddress || "",
            state: userDetail?.state || "",
            city: userDetail?.city || "",
            pinCode: userDetail?.pinCode || "",
            permanentAddress: userDetail?.permanentAddress || "",
            secondaryPermanentAddress: userDetail?.secondaryPermanentAddress || "",
            isSameAsPermanentAddress: userDetail?.isSameAsPermanentAddress ?? false,
            permanentState: userDetail?.permanentState || "",
            permanentCity: userDetail?.permanentCity || "",
            permanentPinCode: userDetail?.permanentPinCode || "",
            bankName: userDetail?.bankName || "",
            ifscCode: userDetail?.ifscCode || "",
            accountHolderName: userDetail?.accountHolderName || "",
            accountNumber: userDetail?.accountNumber || "",
            branchAddress: userDetail?.branchAddress || "",
            nomineeName: userDetail?.nomineeName || "",
            nomineePhone: userDetail?.nomineePhone || "",
            nomineeDob: formatDateForInput(userDetail?.nomineeDob),
            nomineeRelation: userDetail?.nomineeRelation || "",
            nomineeEmail: userDetail?.nomineeEmail || "",
            nomineeAddress: userDetail?.nomineeAddress || "",
            nomineePercentage: userDetail?.nomineePercentage ?? "",
            ecpRelation: userDetail?.ecpRelation || "",
            ecpName: userDetail?.ecpName || "",
            ecpPhone: userDetail?.ecpPhone || "",
            dailyWages: userDetail?.dailyWages ?? "",
            grossSalary: userDetail?.grossSalary ?? "",
            hra: userDetail?.hra ?? "",
            otherAllowances: userDetail?.otherAllowances ?? "",
            user: { id: employee.id },
            userDetail: userDetail || undefined,
            oauthClients: employee.oauthClients || [],
            campusId: employee.campusId || "",
            clientIds: (employee.oauthClients || [])
                .map((oc) => oc.id)
                .filter(Boolean) as string[],
            teamLeadId: employee.teamLead?.id,
        };
    };

    // ── Helper to derive completed steps based on server data & filled fields ──
    const computeOnboardProgress = (employee: IEmployee, docList: any[] = []): string[] => {
        const serverProcess = Array.isArray(employee.onboardingProcess)
            ? [...employee.onboardingProcess]
            : [];
        const derived = new Set<string>(serverProcess);

        if (employee.name && employee.email) {
            derived.add("basic_details");
        }
        if (employee.branchId && employee.siteId && employee.designationId) {
            derived.add("permissions");
        }
        if (Array.isArray(employee.oauthClients) && employee.oauthClients.length > 0) {
            derived.add("application");
        }
        if (employee.userDetail?.aadharNumber || employee.userDetail?.panNumber) {
            derived.add("advance_details");
        }
        if (employee.userDetail?.address && employee.userDetail?.state) {
            derived.add("address");
        }
        if (employee.userDetail?.bankName || employee.userDetail?.accountNumber) {
            derived.add("bank_details");
        }
        if (employee.userDetail?.nomineeName) {
            derived.add("nominee");
        }
        if (Array.isArray(docList) && docList.length > 0) {
            derived.add("documents");
        }
        if (employee.isSalaryCreated) {
            derived.add("salary_detail");
        }

        return Array.from(derived);
    };

    // ── Form ─────────────────────────────────────────────────────────────
    const methods = useForm<EmployeeOnBoard>({
        mode: "onChange",
        reValidateMode: "onChange",
    });

    // ── Fetch employee data (edit or after creation mode) ─────────────────
    useEffect(() => {
        if (!currentId) return;
        const fetchEmployee = async () => {
            setIsLoading(true);
            try {
                const response = await EmployeeOnBoardService.getEmployeeById(currentId);
                if (response?.status && response?.data) {
                    setIsSalaryCreated(response.data.isSalaryCreated || null);
                    const normalized = normalizeEmployeeForForm(response.data);

                    let docList: any[] = [];
                    // Fetch documents from the dedicated API
                    try {
                        const docsResponse = await EmployeeOnBoardService.getEmployeeDocuments(currentId);
                        if (docsResponse?.status && docsResponse?.data) {
                            docList = Array.isArray(docsResponse.data)
                                ? docsResponse.data
                                : docsResponse.data.documents || [];
                            console?.log("doclist", docList)

                            docList.forEach((doc: { type: string; url: string; name?: string, keyName?: string }) => {
                                if (doc.name === "Aadhar Card Front" || doc.type === "AADHAR_FRONT" || doc?.keyName == "aadharFront") normalized.aadharFront = doc.url;
                                if (doc.name === "Aadhar Card Back" || doc.type === "AADHAR_BACK" || doc?.keyName == "aadharBack") normalized.aadharBack = doc.url;
                                if (doc.name === "PAN Card" || doc.type === "PAN_CARD" || doc?.keyName == "panFront") normalized.panFront = doc.url;
                                if (doc.name === "Driving License" || doc.type === "DRIVING_LICENSE" || doc?.keyName == "drivingLicense") normalized.drivingLicense = doc.url;
                                if (doc.name === "Passport" || doc.type === "PASSPORT" || doc?.keyName == "passport") normalized.passport = doc.url;
                                if (doc.name === "Marriage Certificate" || doc?.keyName == "marriageCertificate") normalized.marriageCertificate = doc.url;
                                if (doc.name === "Current Address Proof" || doc?.keyName == "currentAddressProof") normalized.currentAddressProof = doc.url;
                                if (doc.name === "Permanent Address Proof" || doc?.keyName == "permanentAddressProof") normalized.permanentAddressProof = doc.url;
                                if (doc.name === "SSC Marksheet" || doc.name === "SSC" || doc?.keyName == "sscDoc") normalized.sscDoc = doc.url;
                                if (doc.name === "HSC Marksheet" || doc.name === "HSC" || doc?.keyName == "hscDoc") normalized.hscDoc = doc.url;
                                if (doc.name === "Graduation Marksheet" || doc.name === "Graduation" || doc?.keyName == "graduationDoc") normalized.graduationDoc = doc.url;
                                // if (doc.name === "Post Graduation Marksheet" || doc.name === "Post Graduation" || doc?.keyName == "postGraduationDoc") normalized.postGraduationDoc = doc.url;
                                if (doc.name === "Masters Marksheet" || doc.name === "Masters" || doc?.keyName == "mastersDoc") normalized.mastersDoc = doc.url;
                            });
                        }
                    } catch (docError) {
                        console.error("Error fetching employee documents:", docError);
                    }

                    normalized.isTeamLead =
                        loggedInUser?.teamLead?.id === response.data.id;

                    methods.reset(normalized);

                    // Seed progress from server + derived filled fields
                    const computedSteps = computeOnboardProgress(response.data, docList);
                    progress.initFromServer(computedSteps);

                    // Seed verification status from server
                    if (response.data.isVerified) {
                        setIsVerified(true);
                    }
                }
            } catch (error) {
                ThrowError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmployee();
    }, [currentId, methods]);

    // ── File upload helper ───────────────────────────────────────────────
    const uploadFile = async (file: File): Promise<string | null> => {
        const formData = new FormData();
        formData.append("file", file);
        // formData.forEach((value, key) => {
        //     // console.log(`${key}:`, value);
        // });
        try {
            const response = await callApi(`${API_ENDPOINT.fileUpload}/upload`, {
                method: "POST",
                data: formData,
                multipart: true,
            });
            if (response?.status) {
                return response.data.path;
            }
            return null;
        } catch (error) {
            ThrowError(error);
            return null;
        }
    };

    // ── Core submit: create or update employee ──────────────────────────
    const onStepSubmit = async (
        data: Partial<EmployeeOnBoard>,
        stepId: string,
        nextTab?: string,
        customDocuments?: CustomDocumentPayload[],
    ) => {
        setIsLoading(true);
        try {
            // Build the new onboardingProcess array with this step
            const updatedProcess = progress.completedSteps.includes(stepId)
                ? [...progress.completedSteps]
                : [...progress.completedSteps, stepId];

            // Attach onboardingProcess and default type to the payload
            const payload: Partial<EmployeeOnBoard> = {
                ...data,
                type: (data.type ?? "0") as any,
                onboardingProcess: updatedProcess,
            };

            if (currentId) {
                if (stepId === "documents") {
                    // ── Fixed/standard documents ──────────────────────────
                    const documentsArray: { type: string; url: string; name: string, keyName?: string }[] = [];
                    if (data.aadharFront) {
                        documentsArray.push({ type: "PERSONAL", url: data.aadharFront as string, name: "Aadhar Card Front", keyName: "aadharFront" });
                    }
                    if (data.aadharBack) {
                        documentsArray.push({ type: "PERSONAL", url: data.aadharBack as string, name: "Aadhar Card Back", keyName: "aadharBack" });
                    }
                    if (data.panFront) {
                        documentsArray.push({ type: "PERSONAL", url: data.panFront as string, name: "PAN Card", keyName: "panFront" });
                    }
                    if (data.drivingLicense) {
                        documentsArray.push({ type: "PERSONAL", url: data.drivingLicense as string, name: "Driving License", keyName: "drivingLicense" });
                    }
                    if (data.passport) {
                        documentsArray.push({ type: "PERSONAL", url: data.passport as string, name: "Passport", keyName: "passport" });
                    }
                    if (data.marriageCertificate) {
                        documentsArray.push({ type: "PERSONAL", url: data.marriageCertificate as string, name: "Marriage Certificate", keyName: "marriageCertificate" });
                    }
                    if (data.currentAddressProof) {
                        documentsArray.push({ type: "PERSONAL", url: data.currentAddressProof as string, name: "Current Address Proof", keyName: "currentAddressProof" });
                    }
                    if (data.permanentAddressProof) {
                        documentsArray.push({ type: "PERSONAL", url: data.permanentAddressProof as string, name: "Permanent Address Proof", keyName: "permanentAddressProof" });
                    }
                    if (data.sscDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.sscDoc as string, name: "SSC Marksheet", keyName: "sscDoc" });
                    }
                    if (data.hscDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.hscDoc as string, name: "HSC Marksheet", keyName: "hscDoc" });
                    }
                    if (data.graduationDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.graduationDoc as string, name: "Graduation Marksheet", keyName: "graduationDoc" });
                    }
                    // if (data.postGraduationDoc) {
                    //     documentsArray.push({ type: "EDUCATION", url: data.postGraduationDoc as string, name: "Post Graduation Marksheet", keyName: "postGraduationDoc" });
                    // }
                    if (data.mastersDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.mastersDoc as string, name: "Masters Marksheet", keyName: "mastersDoc" });
                    }

                    // ── Custom documents (PERSONAL / EDUCATION / OTHER) ───
                    if (customDocuments && customDocuments.length > 0) {
                        customDocuments.forEach((doc) => {
                            if (doc.file && doc.title) {
                                documentsArray.push({
                                    type: doc.type,
                                    url: doc.file,
                                    name: doc.title,
                                    keyName: doc?.keyName || `${doc.title.trim()}-other`
                                });
                            }
                        });
                    }

                    const response = await EmployeeOnBoardService.saveEmployeeDocuments(
                        currentId,
                        { documents: documentsArray }
                    );
                    if (response?.status) {
                        // Also persist onboardingProcess to editEmployee
                        await EmployeeOnBoardService.editEmployee(
                            { onboardingProcess: updatedProcess },
                            currentId
                        );
                        showToastNotification("success", "Documents updated successfully");
                        progress.markStepComplete(stepId);
                    }
                } else {
                    const response = await EmployeeOnBoardService.editEmployee(
                        payload,
                        currentId,
                    );
                    if (response?.status) {
                        showToastNotification("success", response.message);
                        progress.markStepComplete(stepId);
                    }
                }
            } else {
                const response = await EmployeeOnBoardService.createEmployee(
                    payload as EmployeeOnBoard,
                );
                if (response?.status) {
                    showToastNotification("success", response.message);
                    progress.markStepComplete(stepId);
                    const newEmpId = response?.data?.user?.id;
                    if (newEmpId) {
                        setCreatedId(newEmpId);
                    }
                }
            }

            // Navigation
            if (nextTab) {
                setActiveTab(nextTab);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step handler: validate → collect data → submit ──────────────────
    const handleNextStep = async (
        stepId: string,
        nextTab: string,
        fieldsToValidate: (keyof EmployeeOnBoard)[] = [],
        customDocuments?: CustomDocumentPayload[],
    ) => {
        const isValid = fieldsToValidate.length > 0
            ? await methods.trigger(fieldsToValidate)
            : true;
        if (!isValid) return;

        // Collect only this step's field values
        const stepData: Partial<EmployeeOnBoard> = {};
        fieldsToValidate.forEach((field) => {
            (stepData as Record<string, unknown>)[field] = methods.getValues(field);
        });

        if (currentId) {
            // Edit mode: send only current tab fields
            onStepSubmit(stepData, stepId, nextTab || undefined, customDocuments);
        } else if (activeTab === "tab2") {
            // First create: send all data (Basic + Permissions)
            onStepSubmit(methods.getValues(), stepId, nextTab || undefined, customDocuments);
        } else {
            // Before employee is created (tab1): just move forward
            progress.markStepComplete(stepId);
            setActiveTab(nextTab);
        }
    };

    // ── Client submit (tab3 special case) ────────────────────────────────
    const handleClientSubmit = async (stepId: string) => {
        const isValid = await methods.trigger("clientIds");
        if (!isValid) return;
        const clientIds = methods.getValues("clientIds");
        try {
            setIsLoading(true);

            // Build onboardingProcess with this step
            const updatedProcess = progress.completedSteps.includes(stepId)
                ? [...progress.completedSteps]
                : [...progress.completedSteps, stepId];

            const response = await callApi(
                `${API_ENDPOINT.user}/${currentId}/clients`,
                {
                    method: "POST",
                    data: {
                        clientIds: clientIds,
                        onboardingProcess: updatedProcess,
                    },
                },
            );
            if (response?.status) {
                // Persist the onboardingProcess array to the database via editEmployee
                await EmployeeOnBoardService.editEmployee(
                    { onboardingProcess: updatedProcess },
                    currentId || "",
                );

                showToastNotification("success", response.message);
                progress.markStepComplete(stepId);
                setActiveTab("tab4"); // Navigate to next tab
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Fetch Designation list for Role Verification ─────────────────────
    useEffect(() => {
        const fetchDesignations = async () => {
            try {
                const res = await DesignationService.getDesignation();
                if (res?.status && res?.data?.designation) {
                    const list = (res.data.designation || [])
                        .filter((d: designation) => !isGuestDesignation(d))
                        .map((d: designation) => ({
                            value: d.id || "",
                            label: d.name || "",
                            level: d.level,
                        }));
                    setVerifyModal((prev) => ({ ...prev, designations: list }));
                }
            } catch (err) {
                console.error("Failed to fetch designations:", err);
            }
        };
        fetchDesignations();
    }, []);

    // ── Open Verification Modal ──────────────────────────────────────────
    const handleOpenVerifyModal = () => {
        const currentDesignation = methods.watch("designationId") || "";
        const isCurrentInList = verifyModal.designations.some(
            (d) => d.value === currentDesignation
        );
        setVerifyModal((prev) => ({
            ...prev,
            isOpen: true,
            selectedDesignationId: isCurrentInList ? currentDesignation : "",
            error: "",
        }));
    };

    // ── Finalize Verification with Permanent Designation ─────────────────
    const handleConfirmVerify = async () => {
        if (!currentId) return;

        if (!verifyModal.selectedDesignationId) {
            setVerifyModal((prev) => ({
                ...prev,
                error: "Please select a permanent designation",
            }));
            return;
        }

        const chosenDes = verifyModal.designations.find(
            (d) => d.value === verifyModal.selectedDesignationId
        );
        if (
            chosenDes &&
            isGuestDesignation({
                id: chosenDes.value,
                name: chosenDes.label,
                level: chosenDes.level,
            })
        ) {
            setVerifyModal((prev) => ({
                ...prev,
                error: "Guest cannot be assigned as a permanent designation upon verification. Please choose an active role.",
            }));
            return;
        }

        setIsVerifying(true);
        try {
            const currentValues = methods.getValues();
            const payload: Partial<EmployeeOnBoard> = {
                ...currentValues,
                designationId: verifyModal.selectedDesignationId,
                isVerified: true,
                onboardingProcess: progress.completedSteps,
            };

            const response = await EmployeeOnBoardService.editEmployee(
                payload as any,
                currentId,
            );
            if (response?.status) {
                showToastNotification(
                    "success",
                    "Employee verified and permanent designation assigned successfully!"
                );
                setIsVerified(true);
                methods.setValue("designationId", verifyModal.selectedDesignationId);
                methods.setValue("isVerified" as any, true);
                setVerifyModal((prev) => ({ ...prev, isOpen: false, error: "" }));
                navigate(RoutesLink.superAdminEmplyoeeOnboard);
            } else {
                showToastNotification("danger", response?.message || "Failed to verify employee");
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsVerifying(false);
        }
    };

    // ── Tab Access Validation ──────────────────────────────────────────
    // Tabs 3–9 require both Basic Details (tab1) & Permissions (tab2) to be complete
    const arePrerequisitesComplete = Boolean(
        id ||
        (currentId &&
            progress.isStepComplete("basic_details") &&
            progress.isStepComplete("permissions"))
    );

    const isStepDisabled = (tabKey: string): boolean => {
        // Basic Details (tab1) and Permissions (tab2) can be accessed for initial setup
        if (tabKey === "tab1" || tabKey === "tab2") return false;
        // Remaining tabs (tab3 to tab9) require Basic Details & Permissions to be completed
        return !arePrerequisitesComplete;
    };

    const handleTabChange = (newTab: string) => {
        if (isStepDisabled(newTab)) {
            showToastNotification(
                "warning",
                "Please complete Basic Details (Step 1) and Permissions (Step 2) before proceeding to other sections."
            );
            return;
        }
        setActiveTab(newTab);
    };

    // ── Tab indicator (step-based, not field-based) ──────────────────────
    const renderTabIndicator = (stepId: string, disabled: boolean = false) => {
        if (disabled) {
            return (
                <span
                    className="flex items-center justify-center text-slate-400 opacity-60 shrink-0"
                    title="Locked"
                >
                    <Lock className="w-2.5 h-2.5" />
                </span>
            );
        }
        if (progress.isStepComplete(stepId)) {
            return (
                <span className="flex items-center justify-center bg-emerald-500 text-white rounded-full w-4 h-4 p-0.5 shadow-sm text-[9px] font-bold shrink-0">
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                </span>
            );
        }
        return (
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300/80 shrink-0" />
        );
    };

    // ── Props passed to every tab component ──────────────────────────────
    const tabProps = {
        isLoading,
        isProfile,
        empId: currentId || null,
        handleNextStep,
        uploadFile,
        handleClientSubmit,
        loggedInUserId,
        isSalaryCreated,
        loggedInUser,
        isVerified,
    };

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? "Edit Employee" : "Create Employee"}
                    isBackButtonVisible={true}
                />
                <Card>
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit((data) => onStepSubmit(data, "salary_detail"))}>
                            {/* ── Progress Banner ──────────────────── */}
                            <div className="bg-linear-to-r from-slate-50 via-slate-100/30 to-slate-50 border border-slate-200/80 rounded-xl p-5 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all duration-300">
                                <div className="flex-1 w-full">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-slate-800 tracking-wide">Form Completion Status</span>
                                            <span className="text-[10px] uppercase font-bold tracking-wider text-primary bg-primary/10 px-2.5 py-0.5 rounded border border-primary/20">Profile Strength</span>
                                        </div>
                                        {/* Conditional: Verify Button / Verified Badge / Percentage */}
                                        {isVerified ? (
                                            <span className="flex items-center gap-1.5 text-xs font-extrabold text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full shadow-sm">
                                                <ShieldCheck className="w-4 h-4" />
                                                Verification Completed
                                            </span>
                                        ) : progress.percentage === 100 ? (
                                            <Button
                                                type="button"
                                                onClick={handleOpenVerifyModal}
                                                disabled={isVerifying}
                                                className="flex items-center gap-1.5 text-xs font-extrabold px-4 py-1.5 rounded-full shadow-sm hover:scale-105 transition-transform duration-200"
                                            >
                                                {isVerifying ? <Spinner /> : (
                                                    <>
                                                        <ShieldCheck className="w-4 h-4" />
                                                        Verify Employee
                                                    </>
                                                )}
                                            </Button>
                                        ) : (
                                            <span className="text-xs font-extrabold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full shadow-sm hover:scale-105 transition-transform duration-200">
                                                {progress.percentage}% Completed
                                            </span>
                                        )}
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200/50 h-3 rounded-full overflow-hidden border border-slate-200/40 p-0.5 shadow-inner relative">
                                        <div
                                            className="h-full bg-linear-to-r from-blue-500 via-indigo-500 to-violet-600 rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                                            style={{
                                                width: `${progress.percentage}%`,
                                                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Tabs ─────────────────────────────── */}
                            <Tabs
                                value={activeTab}
                                onValueChange={handleTabChange}
                                className="w-full"
                            >
                                <div className="relative w-full mb-8 group/tabs-container">
                                    {/* Left Arrow */}
                                    {showLeftArrow && (
                                        <div className="absolute left-0 top-0 bottom-0 pl-1.5 pr-10 bg-linear-to-r from-slate-100 via-slate-100/90 to-transparent flex items-center z-10 rounded-l-lg pointer-events-none transition-opacity duration-300">
                                            <button
                                                type="button"
                                                onClick={() => scrollTabs("left")}
                                                className="h-8 w-8 rounded-full bg-white shadow-md border border-slate-200/80 flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:text-primary transition-all duration-200 focus:outline-none pointer-events-auto shadow-slate-300/60 hover:scale-105 active:scale-95"
                                                aria-label="Scroll tabs left"
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Scrollable Tabs List */}
                                    <div
                                        ref={tabsRef}
                                        onScroll={checkScrollButtons}
                                        className="w-full overflow-x-auto overflow-y-hidden scroll-smooth scroll-bar-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                    >
                                        <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner items-center min-w-max justify-start w-full">
                                            {ONBOARD_STEPS.map((step) => {
                                                const disabled = isStepDisabled(step.tabKey);
                                                return (
                                                    <TabsTrigger
                                                        key={step.tabKey}
                                                        value={step.tabKey}
                                                        disabled={disabled}
                                                        className={`${TAB_TRIGGER_CLASS} ${disabled
                                                            ? "opacity-45 cursor-not-allowed pointer-events-none text-slate-400 select-none"
                                                            : ""
                                                            }`}
                                                        title={
                                                            disabled
                                                                ? "Complete Basic Details and Permissions first to access this section"
                                                                : step.label
                                                        }
                                                    >
                                                        <step.icon className="w-4 h-4" />
                                                        {step.label}
                                                        {renderTabIndicator(step.stepId, disabled)}
                                                    </TabsTrigger>
                                                );
                                            })}
                                        </TabsList>
                                    </div>

                                    {/* Right Arrow */}
                                    {showRightArrow && (
                                        <div className="absolute right-0 top-0 bottom-0 pr-1.5 pl-10 bg-linear-to-l from-slate-100 via-slate-100/90 to-transparent flex items-center z-10 rounded-r-lg pointer-events-none transition-opacity duration-300">
                                            <button
                                                type="button"
                                                onClick={() => scrollTabs("right")}
                                                className="h-8 w-8 rounded-full bg-white shadow-md border border-slate-200/80 flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:text-primary transition-all duration-200 focus:outline-none pointer-events-auto shadow-slate-300/60 hover:scale-105 active:scale-95 animate-pulse hover:animate-none"
                                                aria-label="Scroll tabs right"
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* ── Tab Content (dynamic) ────────── */}
                                {ONBOARD_STEPS?.map((step) => {
                                    const Component = TAB_CONTENT_MAP[step.tabKey];
                                    return (
                                        <TabsContent key={step.tabKey} value={step.tabKey}>
                                            <Component {...tabProps} />
                                        </TabsContent>
                                    );
                                })}
                            </Tabs>
                        </form>
                    </FormProvider>
                </Card>

                {/* ── Verify & Finalize Designation Modal ───────────────────────── */}
                <CommonModal
                    title=""
                    show={verifyModal.isOpen}
                    onClose={() => !isVerifying && setVerifyModal((prev) => ({ ...prev, isOpen: false }))}
                    className="max-w-xl sm:max-w-2xl min-h-[560px] p-0 overflow-y-auto rounded-3xl border-0 shadow-2xl flex flex-col justify-between"
                >
                    <div className="relative flex flex-col flex-1">
                        {/* Header decoration */}
                        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:p-7 border-b border-slate-100 rounded-t-3xl shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20 shrink-0">
                                    <ShieldCheck className="h-7 w-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800">
                                        Verify & Activate Employee
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Assign a permanent role to finalize onboarding lifecycle
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 sm:p-7 space-y-6 flex-1 flex flex-col justify-between">
                            <div className="space-y-6">
                                {/* Employee Info Card */}
                                <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className="h-12 w-12 rounded-full bg-slate-200 text-slate-700 font-bold flex items-center justify-center shrink-0 uppercase text-base shadow-inner">
                                            {String(methods.watch("name") || "E")[0]}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-base font-bold text-slate-800 truncate">
                                                {methods.watch("name") || "New Employee"}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate mt-0.5">
                                                {methods.watch("email") || "No email available"}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="text-left sm:text-right shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200/60">
                                        <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Current Role</span>
                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200/60 px-3 py-1 rounded-full mt-1">
                                            {verifyModal.designations.find((d) => d.value === methods.watch("designationId"))?.label || "Guest Employee"}
                                        </span>
                                    </div>
                                </div>

                                {/* Designation Selector */}
                                <div className="space-y-2 relative z-30">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                                        Assign Permanent Designation <span className="text-red-500">*</span>
                                    </label>
                                    <SearchSelect
                                        placeholder="Search & select permanent designation..."
                                        options={verifyModal.designations}
                                        value={verifyModal.selectedDesignationId}
                                        onChange={(val) => {
                                            setVerifyModal((prev) => ({
                                                ...prev,
                                                selectedDesignationId: val,
                                                error: "",
                                            }));
                                        }}
                                        error={verifyModal.error}
                                    />
                                </div>

                                {/* Notice / Helper text */}
                                <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-4 flex gap-3 text-xs text-blue-700 leading-relaxed">
                                    <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                                    <p>
                                        Verifying this employee will finalize their onboarding, grant operational permissions according to the new designation, and mark them as active in the company directory.
                                    </p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100 mt-6">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setVerifyModal((prev) => ({ ...prev, isOpen: false }))}
                                    disabled={isVerifying}
                                    className="h-11 px-6 text-xs font-semibold cursor-pointer"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleConfirmVerify}
                                    disabled={isVerifying}
                                    className="h-11 px-6 text-xs font-bold gap-2 shadow-md shadow-primary/20 cursor-pointer"
                                >
                                    {isVerifying ? (
                                        <>
                                            <Spinner />
                                            <span>Verifying...</span>
                                        </>
                                    ) : (
                                        <>
                                            <UserCheck className="w-4 h-4" />
                                            <span>Confirm & Verify</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CommonModal>
            </div>
        </Layout>
    );
};

export default CreateEmployee;
